<template>
  <main>
    <md-card>
      <md-card-header>
        <div class="md-title">ユーザー管理</div>
      </md-card-header>

      <md-card-content v-if="errorMessages.length">
        <div class="md-layout">
          <div class="md-layout-item md-size-100">
            <flash-message :errors="errorMessages"></flash-message>
          </div>
        </div>
      </md-card-content>
      <loading-spinner v-if="loadingSpinner"></loading-spinner>
      <md-card-content v-else>
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-size-30 md-small-size-100">
            <md-field>
              <label>部⾨</label>
              <md-select name="groupSelected" id="groupSelected" v-model="form.group_id"
                         :disabled="sending || isGroupAdmin()">
                <md-option :value="unspecified">{{ unspecifiedLabel }}</md-option>
                <md-option v-for="(group) in listGroups" v-bind:key="group.group_id"
                           :value="group.group_id">
                  {{ optionLabel(group) }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div class="md-layout-item md-size-30 md-small-size-100">
            <md-field>
              <label>権限</label>
              <md-select v-model="form.role_id" :disabled="sending">
                <md-option :value="unspecified">{{ unspecifiedLabel }}</md-option>
                <md-option v-for="(role) in displayRoles" v-bind:key="role.id"
                           :value="role.id">
                  {{ role.label }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div class="md-layout-item md-size-20 md-small-size-100">
            <md-field>
              <label>キーワード</label>
              <md-input :disabled="sending" name="name" v-model="form.keyword"/>
            </md-field>
          </div>
          <div class="md-layout md-alignment-center-left md-layout-item md-size-20 md-small-size-100">
            <md-button :disabled="sending" @click="doSearch" class="md-primary md-raised" type="submit">
              <md-icon>search</md-icon>
              <span>表示する</span>
            </md-button>
          </div>
        </div>
        <div class="md-layout md-gutter" v-if="hideTotalPriceBlock === false">
          <div class="md-layout-item md-size-55 md-small-size-100"></div>
          <div class="md-layout-item md-size-45 md-small-size-100">
            <md-field>
              <label>現在のアカウント利用料金(税抜)</label>
              <md-input value="0" class="text-right" disabled readonly v-if="sending"></md-input>
              <md-input :value="getTotalPrice" class="text-right" disabled readonly v-else></md-input>
              <span class="md-suffix">円（未確定）</span>
              <loading-spinner-overlay v-if="sending"></loading-spinner-overlay>
              <span class="md-helper-text">
                ※実際のお支払額とは異なる場合がございます。<br/>
                料金の発生につきましては、「<a href="/payment" rel="noopener" tag="md-button" target="_blank">ご利用上の注意</a>」をご確認ください。
              </span>
            </md-field>
          </div>
        </div>

        <md-table>
          <md-table-toolbar>
            <h1 class="md-title">一覧</h1>
          </md-table-toolbar>
          <md-table-row>
            <md-table-head v-for="(column, key) in columns" v-bind:key="'column-key-' + key">
              <a href="#" @click="sortBy(users ,key)"
                 v-bind:class="{'active' : sortKey === key}">{{column.name}}</a>
              <md-icon v-if="sortKey === key && statusSort === 'desc'">arrow_downward</md-icon>
              <md-icon v-else-if="sortKey === key && statusSort === 'asc'">arrow_upward</md-icon>
            </md-table-head>
            <md-table-head v-if="!isTrialUser">
              <md-button :disabled="sending" class="md-primary md-raised" @click="onClickAddButton()">
                <md-icon>add</md-icon>
                <span>新規登録</span>
              </md-button>
            </md-table-head>
          </md-table-row>
          <md-table-row v-if="loadUsers">
            <md-table-cell colspan="8">
              <loading-spinner></loading-spinner>
            </md-table-cell>
          </md-table-row>
          <md-table-row v-else-if="pagination.total === 0">
            <md-table-cell colspan="8">
              <span>{{noDataMessage}}</span>
            </md-table-cell>
          </md-table-row>
          <md-table-row v-else v-for="(user, index) in users" v-bind:key="'column-key-' + index">
            <md-table-cell>{{user.group_name}}</md-table-cell>
            <md-table-cell>{{getRoleName(user.role)}}</md-table-cell>
            <md-table-cell>{{user.name}}</md-table-cell>
            <md-table-cell>{{user.login_id}}</md-table-cell>
            <md-table-cell v-if="!isTrialUser">
              <div class="flexbox">
                <div v-if="isSuperUser() || (isGroupAdmin() && !isRoleSuperUser(user.role))">
                  <router-link v-if="user.shikakumap_deregistered_at === null && user.shikakumap_registered_at !== null"
                               :disabled="sending"
                               :to="{name: 'BackendUserEdit', params: {id: user.account_id.toString()}}"
                               class="md-button md-raised"
                               tag="md-button">
                    <md-icon>edit</md-icon>
                    <span>編集</span>
                  </router-link>
                  <md-button v-else :disabled="true" :md-ripple="false" class="md-button md-raised">
                    <md-icon>edit</md-icon>
                    <span>編集</span>
                  </md-button>
                </div>
                <div>
                  <div v-if="!isRoleSuperUser(user.role)">
                    <md-button v-if="user.shikakumap_deregistered_at === null && user.shikakumap_registered_at !== null"
                               :disabled="sending"
                               class="md-button md-raised md-accent"
                               @click="onUserDeregisterConfirm(user)">
                      <md-icon>delete</md-icon>
                      <span>利用解除</span>
                    </md-button>
                    <md-button v-else-if="user.shikakumap_registered_at === null" :disabled="sending" :md-ripple="false"
                               class="md-button md-accent"
                               @click="onUserRegisterConfirm(user)">
                      <md-icon>add</md-icon>
                      <span>利用登録</span>
                    </md-button>
                    <md-button v-else :disabled="sending" :md-ripple="false" class="md-button md-accent"
                               @click="onUserResumeConfirm(user)">
                      <md-icon>restore_from_trash</md-icon>
                      <span>利用再開</span>
                    </md-button>
                  </div>
                </div>
              </div>
            </md-table-cell>
          </md-table-row>
        </md-table>
        <pagination v-if="!loadUsers && users.length"
                    :total="pagination.total"
                    :per-page="pagination.per_page"
                    :current-page="pagination.current_page"
                    @pagechanged="onPageChanged"
        />
        <delete-modal
          :md-active.sync="showDeregisterModal"
          md-title="ユーザー管理：利用解除の確認"
          :md-content="'以下のユーザーを利用解除します。<br/><p>ユーザー名: ' + deregisterUserName + '</p><br/>※アカウント追加当月内に利用解除した場合は、初月無料が適用されず一月分の料金が発生します。よろしいですか？'"
          confirm-button-class="md-accent md-raised"
          cancel-button-class="md-raised"
          md-confirm-text="利用解除"
          @md-cancel="onDeregisterUserCancel"
          @md-confirm="onDeregisterUserSubmit"/>
        <delete-modal
          :md-active.sync="showRegisterModal"
          md-title="ユーザー管理：利用登録の確認"
          :md-content="'以下のユーザーを利用登録します。<br/><p>ユーザー名: ' + registerUserName + '</p>'"
          confirm-button-class="md-accent md-raised"
          cancel-button-class="md-raised"
          md-confirm-text="利用登録"
          @md-cancel="onUserRegisterCancel"
          @md-confirm="onUserRegisterSubmit"/>
        <delete-modal
          :md-active.sync="showResumeModal"
          md-title="ユーザー管理：利用再開の確認"
          :md-content="'以下のユーザーを利用再開します。<br/><p>ユーザー名: ' + resumeUserName + '</p><br/>※ 利用再開をした時点で月額料金に加算されます。'"
          confirm-button-class="md-accent md-raised"
          cancel-button-class="md-raised"
          md-confirm-text="利用再開"
          @md-cancel="onUserResumeCancel"
          @md-confirm="onUserResumeSubmit"/>
        <delete-modal
          :md-active.sync="showDeleteModal"
          md-title="ユーザー管理：削除の確認"
          :md-content="'以下のユーザーを削除します。<br/><p>ユーザー名: ' + deleteUserName + '</p>'"
          confirm-button-class="md-accent md-raised"
          cancel-button-class="md-raised"
          md-confirm-text="削除"
          @md-cancel="onUserDeleteCancel"
          @md-confirm="onUserDeleteSubmit"/>
        <activation-modal :md-active.sync="showActivationModal"
                          v-bind:t_service_id="tServiceId"
                          v-bind:disabledTServiceId="false"
                          @close="onCloseActivationModal"
                          @activationSuccess="onActivationSuccess"/>
        <md-snackbar :md-active.sync="showSnackBar"
                     :md-duration="snackbarDuration"
                     @md-closed="showSnackBar = false">
          <span>{{ successMessage }}</span>
          <md-button class="md-primary" @click="showSnackBar = false">閉じる</md-button>
        </md-snackbar>
        <md-snackbar :md-active="true" :md-position="'center'" class="md-snackbar-bg__error"
                     v-if="showSnackBarError">
          <flash-message :errors="[errors.outOfBusinessTimeMessage]" :inSnackbar="true"></flash-message>
          <md-button class="md-primary" @click="showSnackBarError = false">閉じる</md-button>
        </md-snackbar>
      </md-card-content>
    </md-card>
    <loading-spinner-overlay v-if="loadingBusinessTime"></loading-spinner-overlay>
  </main>
</template>

<script>
  import Pagination                                     from '../../../components/Pagination';
  import LoadingSpinner                                 from '../../../components/LoadingSpinner';
  import DeleteModal                                    from '../../../components/DeleteModal';
  import FlashMessage                                   from '../../../components/FlashMessage';
  import Common                                         from '../../../modules/common';
  import CommonMixin                                    from '../../../mixins/commonMixin';
  import {apiClientWithUserToken as apiClient, apiList} from '../../../modules/apiClient';
  import axios                                          from 'axios';
  import {mapActions, mapGetters,}                      from 'vuex';
  import ActivationModal                                from '../../../components/ActivationModal';
  import LoadingSpinnerOverlay                          from '../../../components/LoadingSpinnerOverlay';

  // Include deleted users
  const DEFAULT_WITH_TRASHED = 0;

  export default {
    name      : "UserList",
    mixins    : [CommonMixin],
    components: {
      LoadingSpinnerOverlay,
      Pagination,
      LoadingSpinner,
      DeleteModal,
      FlashMessage,
      ActivationModal,
    },
    data() {
      return {
        form               : {
          group_id: null,
          role_id : null,
          keyword : '',
        },
        listGroups         : [],
        listRoles          : [],
        prices             : [],
        tax                : [],
        users              : [],
        activeAccount      : null,
        pagination         : {
          total       : null,
          per_page    : Common.limitPageDefault,
          current_page: 1,
        },
        // Sort config
        sortKey            : 'group_name',
        statusSort         : 'asc',
        columns            : {
          group_name: {name: '部⾨', sort: 'asc'},
          role      : {name: '権限', sort: 'asc'},
          name      : {name: 'ユーザー名', sort: 'asc'},
          account_id: {name: 'ユーザーID', sort: 'asc'},
        },
        // Deregister user
        showActivationModal: false,
        tServiceId         : null,
        showDeregisterModal: false,
        deregisterUser     : [],
        deregisterUserName : null,
        isTrialUser        : true,
        loadUsers          : false,
        showRegisterModal  : false,
        registerUser       : [],
        registerUserName   : null,
        showResumeModal    : false,
        resumeUser         : [],
        resumeUserName     : null,
        showDeleteModal    : false,
        deleteUser         : [],
        deleteUserName     : null,
        errors             : {
          outOfBusinessTimeMessage: Common.messages.errorOutOfBusinessTime,
        },
        loadingBusinessTime: false,
        hideTotalPriceBlock: false,
      }
    },
    mounted() {
      this.applyState();
      this.getSnackBar();
      this.initDisplay();
    },
    computed  : {
      ...mapGetters('users', {
        formData : 'formData',
        sortData : 'sortData',
        isRefresh: 'isRefresh',
        isBack   : 'isBack',
      }),
      /**
       * 総額（税抜）
       * @returns {?string}
       */
      getTotalPrice() {
        return this.calculationAccountFee();
      },
      displayRoles() {
        if (this.isGroupAdmin()) {
          return this.listRoles.filter(role => role.name !== Common.superUserKey);
        }
        return this.listRoles;
      },
    },
    methods   : {
      ...mapActions('users', [
        'saveFormData',
        'saveSortData',
        'saveRefresh',
        'saveBack',
      ]),
      applyState() {
        if (this.isRefresh || this.isBack) {
          if (this.formData !== null) {
            this.pagination.current_page = this.formData.page;
            this.pagination.limit        = this.formData.limit;
            this.form.group_id           = this.formData.group_id;
            this.form.role_id            = this.formData.role_id;
            this.form.keyword            = this.formData.keyword;
          }
          if (this.sortData) {
            this.sortKey    = this.sortData.sortKey;
            this.statusSort = this.sortData.statusSort;
          }
        }
        this.saveRefresh(false);
        this.saveBack(false);
      },
      initDisplay() {
        if (this.isGroupAdmin()) {
          this.form.group_id = Common.getCurrentUserInfo().group.id;
        }
        this.loadingSpinner = true;
        axios
          .all([
            apiClient.get(apiList.accounts.status),
            apiClient.get(apiList.jon.groups.list),
            apiClient.get(apiList.accounts.roles),
            apiClient.get(apiList.payment.price),
            apiClient.get(apiList.accounts.active),
            apiClient.get(apiList.accounts.list, {params: this.createSearchFormData()}),
            apiClient.get(apiList.accounts.list),
          ])
          .then(axios.spread((...responses) => {
            const accountStatusResponse = responses[0];
            const groupListResponse     = responses[1];
            const roleListResponse      = responses[2];
            const priceResponse         = responses[3];
            const activeAccountResponse = responses[4];
            const accountListResponse   = responses[5];
            const allAccountResponse    = responses[6];
            if (this.hasResponseErrors(accountStatusResponse)
              || this.hasResponseErrors(groupListResponse)
              || this.hasResponseErrors(roleListResponse)
              || this.hasResponseErrors(priceResponse)
              || this.hasResponseErrors(activeAccountResponse)
              || this.hasResponseErrors(accountListResponse)
              || this.hasResponseErrors(allAccountResponse)) {
              return;
            }

            this.isTrialUser   = (accountStatusResponse.data.status === Common.trialStatus);
            this.listGroups    = this.parsedGroups(groupListResponse.data.data, 0);
            this.listRoles     = roleListResponse.data.data;
            this.prices        = priceResponse.data.data;
            this.tax           = priceResponse.data.tax;
            this.activeAccount = allAccountResponse.data.accounts.filter(account => account.shikakumap_deregistered_at === null).length;

            // Display account list
            const accountListResult = accountListResponse.data;
            this.setMetaPagination(accountListResult.meta);
            this.users = accountListResult.accounts;
            this.sortBy(this.users);
          }))
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            Common
              .onBusinessTime()
              .then(value => this.onBusinessTime = value)
              .finally(() => this.loadingSpinner = false);
          });
      },
      /**
       * option label groups
       */
      optionLabel(group) {
        if (group.level === 0) {
          return group.name;
        }
        return new Array(group.level + 1).join('　') + group.name;
      },
      /**
       * Set meta pagination
       * @param meta
       */
      setMetaPagination(meta) {
        this.pagination.total        = meta.total;
        this.pagination.per_page     = meta.per_page;
        this.pagination.current_page = meta.current_page;
      },
      createSearchFormData() {
        this.saveFormData({
          page    : this.pagination.current_page,
          limit   : this.pagination.limit,
          group_id: this.form.group_id,
          role_id : this.form.role_id,
          keyword : this.form.keyword,
        });
        this.saveSortData({
          sortKey   : this.sortKey,
          statusSort: this.statusSort,
        });
        return {
          page        : this.pagination.current_page,
          limit       : this.pagination.limit,
          group_id    : this.form.group_id === this.unspecified ? null : this.form.group_id,
          role_id     : this.form.role_id === this.unspecified ? null : this.form.role_id,
          keyword     : this.form.keyword,
          with_trashed: DEFAULT_WITH_TRASHED,
        };
      },
      /**
       * Get active accounts
       */
      getUserList() {
        this.loadUsers = true;
        this.sending   = true;
        apiClient.get(apiList.accounts.list, {params: this.createSearchFormData()})
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }
            const result = response.data;
            this.setMetaPagination(result.meta);
            this.users = result.accounts;
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            this.sortBy(this.users);
            this.sending   = false;
            this.loadUsers = false;
          });
      },
      /**
       * Call deregister user account api
       */
      deregisterUserAccount() {
        this.sending = true;
        apiClient.post(apiList.accounts.deregister.replace(':account_id', this.deregisterUser.account_id))
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }
            if (Math.ceil((this.pagination.total - 1) / this.pagination.per_page) < this.pagination.current_page) {
              this.pagination.current_page = (this.pagination.current_page - 1) || 1;
            }
            this.getUserList();
            this.currentAccountFee();
            this.successMessage = Common.messages.account.deregisterSuccess;
            this.showSnackBar   = true;
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            this.deregisterUser     = null;
            this.deregisterUserName = null;
            this.sending            = false;
          });
      },
      /**
       * Call register user account api
       */
      registerUserAccount() {
        this.sending = true;
        apiClient.post(apiList.accounts.register.replace(':account_id', this.registerUser.account_id))
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }
            if (Math.ceil((this.pagination.total - 1) / this.pagination.per_page) < this.pagination.current_page) {
              this.pagination.current_page = (this.pagination.current_page - 1) || 1;
            }
            this.getUserList();
            this.successMessage = Common.messages.account.registerSuccess;
            this.showSnackBar   = true;
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            this.registerUser     = null;
            this.registerUserName = null;
            this.sending          = false;
          });
      },
      /**
       * Call resume user account api
       */
      resumeUserAccount() {
        this.sending = true;
        apiClient.post(apiList.accounts.resume.replace(':account_id', this.resumeUser.account_id))
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }
            if (Math.ceil((this.pagination.total - 1) / this.pagination.per_page) < this.pagination.current_page) {
              this.pagination.current_page = (this.pagination.current_page - 1) || 1;
            }
            this.getUserList();
            this.successMessage = Common.messages.account.resumeSuccess;
            this.showSnackBar   = true;
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            this.resumeUser     = null;
            this.resumeUserName = null;
            this.sending        = false;
          });
      },
      /**
       * Call delete user account api
       */
      deleteUserAccount() {
        this.sending = true;
        apiClient.delete(apiList.accounts.delete.replace(':account_id', this.deleteUser.account_id))
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }
            if (Math.ceil((this.pagination.total - 1) / this.pagination.per_page) < this.pagination.current_page) {
              this.pagination.current_page = (this.pagination.current_page - 1) || 1;
            }
            this.getUserList();
            this.currentAccountFee();
            this.successMessage = Common.messages.account.deregisterSuccess;
            this.showSnackBar   = true;
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            this.deleteUser     = null;
            this.deleteUserName = null;
            this.sending        = false;
          });
      },
      doSearch() {
        this.hideTotalPriceBlock     = !((this.form.role_id === null || this.form.role_id === this.unspecified)
          && (this.form.keyword === null || this.form.keyword === ''));
        this.pagination.current_page = 1;
        this.getUserList();
      },
      onPageChanged(page) {
        this.pagination.current_page = page;
        this.getUserList();
      },
      onClickAddButton() {
        this.loadingBusinessTime = true;
        Common.onBusinessTime()
          .then(value => this.onBusinessTime = value)
          .then(() => {
            this.loadingBusinessTime = false;
            if (this.onBusinessTime === false) {
              this.showSnackBarError = true;
              return;
            }
            this.$router.push({
              name: 'BackendUserNew',
            }).then();
          });
      },
      onUserDeregisterConfirm(userDeregister) {
        this.showDeregisterModal = true;
        this.deregisterUserName  = userDeregister.name;
        this.deregisterUser      = userDeregister;
      },
      onDeregisterUserCancel() {
        this.showDeregisterModal = false;
        this.deregisterUserName  = null;
        this.deregisterUser      = [];
      },
      onDeregisterUserSubmit() {
        this.showDeregisterModal = false;
        this.deregisterUserAccount();
      },
      onUserRegisterConfirm(userRegister) {
        this.registerUser        = [];
        this.resumeUser          = [];
        this.loadingBusinessTime = true;
        Common.onBusinessTime()
          .then(value => this.onBusinessTime = value)
          .then(() => {
            this.loadingBusinessTime = false;
            if (this.onBusinessTime === false) {
              this.showSnackBarError = true;
              return;
            }
            this.showRegisterModal = true;
            this.registerUserName  = userRegister.name;
            this.registerUser      = userRegister;
          });
      },
      onUserRegisterCancel() {
        this.showRegisterModal = false;
        this.registerUserName  = null;
        this.registerUser      = [];
      },
      onUserRegisterSubmit() {
        this.showRegisterModal   = false;
        this.showActivationModal = true;
        this.tServiceId          = null;
      },
      onUserResumeConfirm(userResume) {
        this.registerUser        = [];
        this.resumeUser          = [];
        this.loadingBusinessTime = true;
        Common.onBusinessTime()
          .then(value => this.onBusinessTime = value)
          .then(() => {
            this.loadingBusinessTime = false;
            if (this.onBusinessTime === false) {
              this.showSnackBarError = true;
              return;
            }
            this.showResumeModal = true;
            this.resumeUserName  = userResume.name;
            this.resumeUser      = userResume;
          });
      },
      onUserResumeCancel() {
        this.showResumeModal = false;
        this.resumeUserName  = null;
        this.resumeUser      = [];
      },
      onUserResumeSubmit() {
        this.showResumeModal     = false;
        this.showActivationModal = true;
        this.tServiceId          = null;
      },
      onUserDeleteConfirm(userDelete) {
        this.loadingBusinessTime = true;
        Common.onBusinessTime()
          .then(value => this.onBusinessTime = value)
          .then(() => {
            this.loadingBusinessTime = false;
            if (this.onBusinessTime === false) {
              this.showSnackBarError = true;
              return;
            }
            this.showDeleteModal = true;
            this.deleteUserName  = userDelete.name;
            this.deleteUser      = userDelete;
          });
      },
      onUserDeleteCancel() {
        this.showDeleteModal = false;
        this.deleteUserName  = null;
        this.deleteUser      = [];
      },
      onUserDeleteSubmit() {
        this.showDeleteModal = false;
        this.deleteUserAccount();
      },
      onCloseActivationModal() {
        this.showActivationModal = false;
      },
      onActivationSuccess(tServiceId) {
        this.showActivationModal = false;
        this.tServiceId          = tServiceId;
        this.updateTService();
      },
      getRoleName(roleId) {
        const role = this.listRoles.filter(function (role) {
          return role.id === roleId;
        });
        return role.length > 0 ? role[0].label : null;
      },
      /**
       * Is role super user
       * @param roleId
       */
      isRoleSuperUser(roleId) {
        const role = this.listRoles.filter(function (role) {
          return role.id === roleId;
        });

        return role.length > 0 ? role[0].name === Common.superUserKey : false;
      },
      /**
       * 現在のアカウント利用料金(税込)を算出する
       */
      currentAccountFee() {
        this.sending = true;
        apiClient.get(apiList.accounts.list)
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }
            this.activeAccount = response.data.accounts.filter(account => account.shikakumap_deregistered_at === null);
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            this.sending = false;
          });
      },
      /**
       * 現在のアカウント利用料金(税抜)を算出する
       */
      calculationAccountFee() {
        const billingTotal = Common.priceCalculator(this.prices, this.activeAccount);
        const unitPrice    = Common.unitPriceCalculator(billingTotal, this.activeAccount);
        const totalPrice   = Common.consumptionTaxCalculator(unitPrice, this.activeAccount);
        const activeUser   = this.users.filter(user => user.shikakumap_deregistered_at === null).length;
        if (activeUser === 0) {
          return '0';
        }
        return Common.numberFormat(Math.floor(Number(totalPrice) / this.activeAccount * activeUser));
      },
      updateTService() {
        this.sending    = true;
        const accountId = this.resumeUser.account_id ? this.resumeUser.account_id : this.registerUser.account_id
        const url       = apiList.accounts.update.replace(':account_id', accountId)
        apiClient
          .put(url, {
            t_service_id: this.tServiceId,
          })
          .then(response => {
            if (this.hasResponseErrors(response)) {
              this.errorMessages = [this.getValidationResponseMessage('t_service_id')];
              this.sending       = false;
              return;
            }

            if (this.resumeUser.account_id) {
              this.resumeUserAccount();
            } else {
              this.registerUserAccount();
            }
          })
          .catch(error => {
            this.errorMessages = [error.message];
            this.sending       = false;
          });
      },
    },
  }
</script>

<style lang="scss" scoped>
  .md-theme-default a:not(.md-button) {
    color: black;
  }

  a.active {
    font-weight: bold;
    color: black;
  }

  .md-toolbar {
    width: 50%;
  }

  .md-table-content {
    .md-table-row {
      .md-table-head {
        &:nth-child(1) {
          min-width: 120px;
        }

        &:nth-child(2) {
          min-width: 80px;
        }

        &:nth-child(3) {
          min-width: 120px;
        }

        &:nth-child(4) {
          min-width: 160px;
        }

        &:nth-child(5) {
          min-width: 600px;
        }
      }

      ::v-deep(.md-table-cell-container) {
        padding: 6px 16px 6px 16px;
      }
    }
  }
</style>
